import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAlternates } from "../lib/alt";
import { langFromPath } from "../lib/lang";
import { LANGS } from "../lib/routes";
import Home from "./Home";
import ContentPage from "./ContentPage";
import BlogList from "./BlogList";
import Contact from "./Contact";
import Upload from "./Upload";
import ServicesOverview from "./ServicesOverview";
import NotFound from "./NotFound";

const VIEWS = { home: Home, services: ServicesOverview, contact: Contact, blog: BlogList, upload: Upload };
const BOT = /bot|crawl|spider|slurp|lighthouse|headless/i;

function autoLanguage(path, alternates, navigate) {
  if (sessionStorage.getItem("tl_auto") || BOT.test(navigator.userAgent)) return false;
  sessionStorage.setItem("tl_auto", "1");
  const pref = localStorage.getItem("tl_lang") || (navigator.languages?.[0] || navigator.language || "es").slice(0, 2).toLowerCase();
  if (!LANGS.includes(pref) || pref === langFromPath(path)) return false;
  const target = alternates.find((a) => a.lang === pref)?.path;
  if (!target) return false;
  navigate(target, { replace: true });
  return true;
}

export default function Resolver() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setAlternates } = useAlternates();
  const [state, setState] = useState({ status: "loading" });
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, status: s.data ? "refresh" : "loading" }));
    api.get("/content/resolve", { params: { path: pathname } })
      .then((res) => {
        if (!alive) return;
        if (res.data?.redirect) { setState({ status: "redirect", to: res.data.redirect }); return; }
        const alts = Array.isArray(res.data?.alternates) ? res.data.alternates : [];
        if (autoLanguage(pathname, alts, navigate)) return;
        setAlternates(alts);
        setState({ status: "ok", data: res.data });
      })
      .catch((e) => { if (alive) { setAlternates([]); setState({ status: e?.response?.status === 404 ? "404" : "error" }); } });
    return () => { alive = false; };
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === "redirect") return <Navigate to={state.to} replace />;
  if (state.status === "404" || state.status === "error") return <NotFound />;
  if (!state.data) return <div className="min-h-screen" data-testid="page-loading" />;
  const { doc, alternates, related } = state.data;
  const View = VIEWS[doc.template] || ContentPage;
  return <View key={doc.path} doc={doc} alternates={alternates || []} related={related || []} />;
}
